/**
 * The language switcher.
 *
 * ## Why it is visible and not automatic-only
 *
 * Most of this site's donors are outside the country. A browser-language guess
 * gets `en-GB` right and `en-US`, `fr`, `de` and `ar-SA` all wrong in ways the
 * donor cannot see and cannot fix. A switcher is the only thing that makes the
 * choice theirs, so it is on every page rather than buried in a menu.
 *
 * Three states, in order:
 *
 *   1. an explicit choice, remembered in `localStorage`;
 *   2. the browser's own preference, restricted to what we ship;
 *   3. Arabic, the fallback and the product's primary language.
 *
 * The choice is remembered in `localStorage` and not in a cookie because no
 * server-side rendering needs it and a preference is not worth a cookie.
 */
import {
  applyDocumentLocale,
  i18n,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from '@qalam/shared/frontend';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY = 'qalam.locale';

/** The locale to start in: an explicit past choice, else the browser, else Arabic. */
export function resolveInitialLocale(
  stored: string | null,
  navigatorLanguage: string | undefined,
): SupportedLocale {
  if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
    return stored as SupportedLocale;
  }
  const raw = navigatorLanguage ?? (typeof navigator === 'undefined' ? '' : navigator.language);
  const base = (raw.split('-')[0] ?? '').toLowerCase();
  return (SUPPORTED_LOCALES as readonly string[]).includes(base) ? (base as SupportedLocale) : 'ar';
}

function readStored(): string | null {
  try {
    return globalThis.localStorage?.getItem(STORAGE_KEY) ?? null;
  } catch {
    // Private browsing, a blocked origin, a quota error: all of them mean "no
    // stored preference", which is a perfectly good state to be in.
    return null;
  }
}

function store(locale: SupportedLocale): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, locale);
  } catch {
    // Not being able to remember is a minor annoyance, never an error worth
    // showing a donor.
  }
}

/**
 * Set the locale everywhere it matters.
 *
 * i18next for the strings, `<html lang>` and `<html dir>` for the browser's own
 * bidi and font handling. Changing one without the other produces a page whose
 * text is translated but whose layout is mirrored the wrong way round, which is
 * worse than either state alone.
 */
export async function setLocale(locale: SupportedLocale): Promise<void> {
  store(locale);
  if (typeof document !== 'undefined') applyDocumentLocale(locale);
  await i18n.changeLanguage(locale);
}

/** Initialise the app's language. Called once, before the first render. */
export function useInitialLocale(): SupportedLocale {
  const [locale] = useState<SupportedLocale>(() =>
    resolveInitialLocale(
      readStored(),
      typeof navigator === 'undefined' ? undefined : navigator.language,
    ),
  );
  useEffect(() => {
    void setLocale(locale);
  }, [locale]);
  return locale;
}

const NAMES: Record<SupportedLocale, string> = {
  ar: 'العربية',
  // Each language is written in itself, which is the only form a donor can
  // recognise: "English" is not what an English reader is looking for in a list
  // that also says "العربية".
  en: 'English',
};

export function LanguageSwitcher(): React.ReactElement {
  const { t, i18n: instance } = useTranslation();
  const current = (instance.resolvedLanguage?.split('-')[0] ?? 'ar') as SupportedLocale;
  return (
    // A real form control rather than a div of buttons: it is keyboard
    // navigable, announced, and works without JavaScript styling assumptions.
    <label className="lang-switch" data-testid="language-switcher">
      <span className="lang-switch__label">{t('app.language')}</span>
      <select
        value={current}
        data-testid="language-select"
        aria-label={t('app.language')}
        onChange={(event) => void setLocale(event.target.value as SupportedLocale)}
      >
        {SUPPORTED_LOCALES.map((code) => (
          <option key={code} value={code}>
            {NAMES[code]}
          </option>
        ))}
      </select>
    </label>
  );
}
